import { VariantOption as HorizonOption } from '@thg-commerce/enterprise-graphql/horizon-schema'

import { SiteProperties } from '../../../../graphql/horizon-schema'
import { Price } from '../../types'
import {
  Choice,
  ChoiceIcon,
  choiceTransformer,
  getColourHexcode,
  TransformedChoice,
} from '../choice'
import { TransformedOption } from '../option'

import { Variant } from './types'

export interface ChoiceMapProps {
  variants: Variant[]
  horizonOptions: HorizonOption[]
  orderedOptionKeys: string[]
  showOutOfStockAsDisabled: boolean
  siteProperties: SiteProperties
  displayNotifyWhenInStock: boolean
  optionKeysToApplyAttribute?: string[]
  icon?: ChoiceIcon | null
  linkedOnOptionKey?: string | null
  range?: {
    priceRange?: {
      minimum: Price
      maximum: Price
    }
    rrpRange?: {
      minimum: Price
      maximum: Price
    }
  }
}

interface ChoiceOptionMap {
  [choiceKey: string]: TransformedOption
}

export interface FallbackChoiceMap {
  [optionKey: string]: { [choiceKey: string]: TransformedChoice }
}

export type ChoiceMap =
  | { [choiceKey: string]: ChoiceOptionMap }
  | ChoiceOptionMap

export interface ChoiceMapTransformer {
  choiceMap: ChoiceMap
  fallbackChoiceMap: FallbackChoiceMap
}
const createNewHashmapKey = (hashmap: {}, key: string, value: Object) => {
  if (!hashmap[key]) {
    hashmap[key] = value
  }

  return hashmap
}

const createChoiceMapping = (
  hashmap: {},
  choice: Choice,
  otherChoice: Choice,
) => {
  createNewHashmapKey(hashmap, choice.key, {})
  createNewHashmapKey(hashmap[choice.key], otherChoice.optionKey, [])

  return hashmap
}

export const sortArray = (props: {
  horizonOptions: HorizonOption[]
  choiceOptionKey: string
  arrayToSort: TransformedChoice[]
}) => {
  const horizonOrderedOptionChoices = props.horizonOptions.reduce<string[]>(
    (horizonOptionAccumulator, option) => {
      if (option.key !== props.choiceOptionKey) {
        return horizonOptionAccumulator
      }
      option.choices.forEach((horizonChoice) =>
        horizonOptionAccumulator.push(horizonChoice.key),
      )
      return horizonOptionAccumulator
    },
    [],
  )

  const sortedArray = props.arrayToSort.sort((choiceA, choiceB) =>
    horizonOrderedOptionChoices.indexOf(choiceA.key) >
    horizonOrderedOptionChoices.indexOf(choiceB.key)
      ? 1
      : -1,
  )

  return sortedArray
}

const addToChoiceMap = (props: {
  variant: Variant
  choice: Choice
  otherChoice: Choice
  accumulator: ChoiceMapTransformer
  displayNotifyWhenInStock: boolean
  showOutOfStockAsDisabled?: boolean | null
  optionKeysToApplyAttribute?: string[]
  icon?: ChoiceIcon | null
  horizonOptions: HorizonOption[]
  siteProperties: SiteProperties
  linkedOnOptionKey?: string | null
}) => {
  const storedChoices =
    props.accumulator.choiceMap[props.choice.key][props.otherChoice.optionKey]

  const storedKeys = new Set<string>(storedChoices.map((choice) => choice.key))

  if (!storedKeys.has(props.otherChoice.key)) {
    storedChoices.push(
      choiceTransformer({
        variant: props.variant,
        optionKeysToApplyAttribute: props.optionKeysToApplyAttribute,
        icon: props.icon,
        showOutOfStockAsDisabled: props.showOutOfStockAsDisabled,
        siteProperties: props.siteProperties,
        choice: props.otherChoice,
        linkedOnOptionKey: props.linkedOnOptionKey,
        displayNotifyWhenInStock: props.displayNotifyWhenInStock,
      }),
    )

    sortArray({
      horizonOptions: props.horizonOptions,
      arrayToSort: storedChoices,
      choiceOptionKey: props.otherChoice.optionKey,
    })
  }

  return props.accumulator
}

const addToFallbackChoiceMap = (props: {
  fallbackChoiceMap: FallbackChoiceMap
  choice: Choice
  siteProperties: SiteProperties
  optionKeysToApplyAttribute?: string[] | null
  thumbnailImage?: string | null
  linkedOnOptionKey?: string | null
}) => {
  if (!props.fallbackChoiceMap[props.choice.optionKey][props.choice.key]) {
    const unavailableSiteProperty =
      props.siteProperties?.['product.productoptions.dropdown.unavailable.text']
        .value
    const title = unavailableSiteProperty
      ? `${props.choice.title} - ${unavailableSiteProperty}`
      : props.choice.title

    props.fallbackChoiceMap[props.choice.optionKey][props.choice.key] = {
      title: props.choice.title,
      key: props.choice.key,
      displayText: title,
      useDisabledStyling: true,
      disabled:
        props.choice.optionKey === props.linkedOnOptionKey ? false : true,
      attribute: props.optionKeysToApplyAttribute?.includes(
        props.choice.optionKey,
      ),
      icon: null,
      image: props.thumbnailImage ? props.thumbnailImage : null,
      colour: getColourHexcode(props.choice?.colour),
      selected: false,
      optionKey: props.choice.optionKey,
    }
  }
}

export const choiceMapTransformer = (
  props: ChoiceMapProps,
): ChoiceMapTransformer => {
  const choiceMapConfig = {
    showOutOfStockAsDisabled: props.showOutOfStockAsDisabled,
    optionKeysToApplyAttribute: props.optionKeysToApplyAttribute,
    horizonOptions: props.horizonOptions,
    icon: props.icon,
    siteProperties: props.siteProperties,
  }

  return props.variants.reduce<ChoiceMapTransformer>(
    (accumulator, variant) => {
      const orderedVariantChoices = props.orderedOptionKeys.map((optionKey) =>
        variant.choices.find((choice) => choice.optionKey === optionKey),
      )

      orderedVariantChoices.forEach((choice) => {
        orderedVariantChoices.forEach((otherChoice) => {
          if (!choice || !otherChoice) {
            return
          }

          createChoiceMapping(accumulator.choiceMap, choice, otherChoice)
          createChoiceMapping(accumulator.choiceMap, otherChoice, choice)
          createChoiceMapping(accumulator.choiceMap, choice, choice)

          addToChoiceMap({
            ...choiceMapConfig,
            accumulator,
            variant,
            choice,
            otherChoice,
            linkedOnOptionKey: props.linkedOnOptionKey,
            displayNotifyWhenInStock: props.displayNotifyWhenInStock,
          })

          addToChoiceMap({
            ...choiceMapConfig,
            accumulator,
            variant,
            choice: otherChoice,
            otherChoice: choice,
            linkedOnOptionKey: props.linkedOnOptionKey,
            displayNotifyWhenInStock: props.displayNotifyWhenInStock,
          })

          addToChoiceMap({
            ...choiceMapConfig,
            accumulator,
            variant,
            choice,
            otherChoice: choice,
            linkedOnOptionKey: props.linkedOnOptionKey,
            displayNotifyWhenInStock: props.displayNotifyWhenInStock,
          })

          if (!accumulator.fallbackChoiceMap[choice.optionKey]) {
            accumulator.fallbackChoiceMap[choice.optionKey] = {}
          }

          addToFallbackChoiceMap({
            choice,
            siteProperties: props.siteProperties,
            optionKeysToApplyAttribute: props.optionKeysToApplyAttribute,
            fallbackChoiceMap: accumulator.fallbackChoiceMap,
            thumbnailImage: variant.images[0]?.thumbnail,
            linkedOnOptionKey: props.linkedOnOptionKey,
          })
        })
      })

      return accumulator
    },
    { choiceMap: {}, fallbackChoiceMap: {} },
  )
}
